import { apiConstants } from "@data/constants/apiConstants"
import { authConstants } from "@data/constants/authConstants"
import { liveModeConstants } from "@data/constants/liveModeConstants"
import { authService } from "@data/services/authService"

/**
 * La mémoire du fil de la séance du tuteur (la salle du design system) : ce qui s'est passé pendant le
 * live et que le serveur ne garde pas — la vie de chaque carte (née, périmée, décidée), les bulles du
 * tuteur. Les signaux eux-mêmes arrivent par la salle (`LKEvents`, dans `liveMode`) ; les résumés du
 * champion se relisent du serveur.
 *
 * Décision du 02/11 (tech-derisk, point 8) : côté navigateur seulement, persistée avec `data` comme
 * `tutorEventsPanel`, **rangée par compte et par séance** (clé `compte·séance`, jamais par élève : le
 * fil est unique, chaque entrée porte son élève). Seule l'entrée de la clé en cours se lit. Elle se
 * vide à la fin du live (`RESET_LIVE_MODE`), à la déconnexion et au changement de compte (les mêmes
 * actions que `tutorEventsPanel.js`), et au montage de la salle de toute autre séance (`PRUNE`). Elle
 * est bornée : une séance garde ses dernières entrées, et le store ne garde qu'une poignée de séances.
 */

export const TUTOR_LIVE_SESSION_ACTIONS = {
    PRUNE: "TUTOR_LIVE_SESSION/PRUNE",
    SYNC: "TUTOR_LIVE_SESSION/SYNC",
    APPEND: "TUTOR_LIVE_SESSION/APPEND",
    DECIDE: "TUTOR_LIVE_SESSION/DECIDE",
    SEEN: "TUTOR_LIVE_SESSION/SEEN",
}

/** Au-delà, les plus anciennes entrées d'une séance tombent (le plafond des 200 cartes écartées d'aujourd'hui). */
export const MAX_ENTRIES_PER_SESSION = 300
/**
 * Le mode du son et l'activité devant chaque élève (le ruban de la séance) vivent à part, sous leur propre plafond :
 * des bascules fréquentes ne chassent jamais une carte, une trace ou une bulle du fil.
 */
export const MAX_TIMELINE_PER_SESSION = 400
const TIMELINE_KINDS = new Set(["mode", "focus", "app"])
/** Une séance en cours, et au plus deux restes (un rechargement entre deux lives) avant le `PRUNE` du montage. */
export const MAX_SESSIONS = 3

const initialState = { sessions: {} }

/** La clé d'une mémoire : le compte et la séance. Sans l'un des deux, il n'y a rien à ranger. */
export function tutorLiveSessionKey(accountId, liveSessionId) {
    if (accountId == null || liveSessionId == null) return null
    return `${accountId}·${liveSessionId}`
}

function emptySession(at) {
    return { createdAt: at, entries: [], seen: {} }
}

function withSession(state, key, at, update) {
    if (!key) return state
    const current = state.sessions?.[key] ?? emptySession(at ?? Date.now())
    const next = update(current)
    if (next === current) return state
    const sessions = { ...state.sessions, [key]: next }
    const keys = Object.keys(sessions)
    if (keys.length > MAX_SESSIONS) {
        // La plus ancienne séance part la première : celle qu'on écrit reste.
        keys.filter((k) => k !== key)
            .sort(
                (a, b) =>
                    (sessions[a].createdAt ?? 0) - (sessions[b].createdAt ?? 0)
            )
            .slice(0, keys.length - MAX_SESSIONS)
            .forEach((k) => delete sessions[k])
    }
    return { ...state, sessions }
}

function bounded(entries) {
    return entries.length > MAX_ENTRIES_PER_SESSION
        ? entries.slice(-MAX_ENTRIES_PER_SESSION)
        : entries
}

export function tutorLiveSession(state = initialState, action) {
    switch (action.type) {
        case authConstants.LOGOUT_401:
        case authConstants.RESET_AUTH_STATE:
        case authService.logout.name + apiConstants._SUCCESS:
        case liveModeConstants.RESET_LIVE_MODE:
            return initialState

        // Au montage de la salle : toute autre séance (une autre occurrence, un autre compte) s'efface.
        case TUTOR_LIVE_SESSION_ACTIONS.PRUNE: {
            const keep = action.payload?.key
            const kept =
                keep && state.sessions?.[keep]
                    ? { [keep]: state.sessions[keep] }
                    : {}
            if (
                Object.keys(state.sessions ?? {}).length ===
                Object.keys(kept).length
            )
                return state
            return { ...state, sessions: kept }
        }

        // Les cartes qui naissent (`opened`) et celles dont le signal est tombé (`closed`, avec la trace de
        // leur fin) : un seul envoi par changement.
        case TUTOR_LIVE_SESSION_ACTIONS.SYNC: {
            const { key, at, opened = [], closed = [] } = action.payload ?? {}
            if (opened.length === 0 && closed.length === 0) return state
            return withSession(state, key, at, (session) => {
                const known = new Set(session.entries.map((entry) => entry.id))
                const ending = new Map(closed.map((end) => [end.id, end]))
                let changed = false
                const entries = session.entries.map((entry) => {
                    const end = ending.get(entry.id)
                    if (end == null || entry.state?.kind !== "open")
                        return entry
                    changed = true
                    return {
                        ...entry,
                        state: { kind: "stale", at: end.at, text: end.text, ...(end.escalated && { escalated: true }) },
                    }
                })
                const fresh = opened.filter((entry) => !known.has(entry.id))
                if (!changed && fresh.length === 0) return session
                return { ...session, entries: bounded([...entries, ...fresh]) }
            })
        }

        // Une entrée du tuteur (sa bulle, une trace) : ajoutée une fois. Le mode et l'activité d'un élève vont à la
        // chronologie, à part.
        case TUTOR_LIVE_SESSION_ACTIONS.APPEND: {
            const { key, at, entry } = action.payload ?? {}
            if (!entry?.id) return state
            if (TIMELINE_KINDS.has(entry.kind)) {
                return withSession(state, key, at, (session) => {
                    const timeline = session.timeline ?? []
                    if (timeline.some((e) => e.id === entry.id)) return session
                    const next = [...timeline, entry]
                    return {
                        ...session,
                        timeline:
                            next.length > MAX_TIMELINE_PER_SESSION
                                ? next.slice(-MAX_TIMELINE_PER_SESSION)
                                : next,
                    }
                })
            }
            return withSession(state, key, at, (session) =>
                session.entries.some((e) => e.id === entry.id)
                    ? session
                    : {
                          ...session,
                          entries: bounded([...session.entries, entry]),
                      }
            )
        }

        // Le geste du tuteur sur une carte ouverte : elle sort du bac, avec ce qu'il a fait.
        case TUTOR_LIVE_SESSION_ACTIONS.DECIDE: {
            const { key, id, at, text } = action.payload ?? {}
            if (!id) return state
            return withSession(state, key, at, (session) => {
                let changed = false
                const entries = session.entries.map((entry) => {
                    if (entry.id !== id || entry.state?.kind !== "open")
                        return entry
                    changed = true
                    return { ...entry, state: { kind: "decided", at, text } }
                })
                return changed ? { ...session, entries } : session
            })
        }

        // Le dernier passage du tuteur sur un élève : le repère « Depuis ton dernier passage ».
        case TUTOR_LIVE_SESSION_ACTIONS.SEEN: {
            const { key, studentId, at } = action.payload ?? {}
            if (studentId == null) return state
            return withSession(state, key, at, (session) =>
                session.seen?.[studentId] === at
                    ? session
                    : { ...session, seen: { ...session.seen, [studentId]: at } }
            )
        }

        default:
            return state
    }
}

export const pruneTutorLiveSession = (key) => ({
    type: TUTOR_LIVE_SESSION_ACTIONS.PRUNE,
    payload: { key },
})

export const syncTutorLiveSession = (
    key,
    { opened = [], closed = [] },
    at = Date.now()
) => ({
    type: TUTOR_LIVE_SESSION_ACTIONS.SYNC,
    payload: { key, at, opened, closed },
})

export const appendTutorLiveSession = (key, entry, at = Date.now()) => ({
    type: TUTOR_LIVE_SESSION_ACTIONS.APPEND,
    payload: { key, at, entry },
})

export const decideTutorLiveSession = (key, id, text, at = Date.now()) => ({
    type: TUTOR_LIVE_SESSION_ACTIONS.DECIDE,
    payload: { key, id, at, text },
})

export const seenTutorLiveSession = (key, studentId, at = Date.now()) => ({
    type: TUTOR_LIVE_SESSION_ACTIONS.SEEN,
    payload: { key, studentId, at },
})
